<template>
    <v-card class="d-flex align-center justify-center" light outlined>
        <div>
            <v-card-title class="text-center" style="background-color: #d2d7e1">
                <v-list-item>
                    <v-list-item-title><h2>{{ $t('titles.normal') }}</h2></v-list-item-title>
                    <v-spacer/>
                    <v-list-item-action>
                        <v-icon class="icon" @click="closeOverlay" icon>fa-times</v-icon>
                    </v-list-item-action>
                </v-list-item>
            </v-card-title>
            <v-card-text class="px-7 pt-3">
                <v-form v-model="valid">
                    <v-content>
                        <v-row>
                            <v-col>
                                <v-autocomplete :items="this.$store.state.user.assets" :disabled="trade.automaticallyAdded" item-text="ticker" item-value="id" :label="$t('items.asset')" :return-object="false"
                                                outlined prepend-icon="fa-euro-sign" :rules="assetRule" v-model="tradeAsset"/>
                            </v-col>
                            <v-col>
                                <v-autocomplete :items="brokerAccounts" :disabled="trade.automaticallyAdded" item-value="id" :return-object="false"
                                                :label="$t('items.account')" outlined prepend-icon="fa-university" :rules="brokerAccountRule" v-model="tradeBrokerAccount">
                                    <template v-slot:selection="data">{{ (data.item.identifier !== '')? data.item.identifier : data.item.broker.name }}</template>
                                    <template v-slot:item="data">{{ (data.item.identifier !== '')? `${data.item.broker.name} · ${data.item.identifier}` :  data.item.broker.name }}</template>
                                </v-autocomplete>
                            </v-col>
                            <v-col>
                                <v-select :items="this.$store.state.sides" :disabled="trade.automaticallyAdded" :label="$t('items.side')" outlined prepend-icon="fa-arrows-alt-v" v-model="tradeSide"/>
                            </v-col>
                        </v-row>

                        <v-divider class="mt-n5 mb-3"/>

                        <v-row>
                            <v-col>
                                <v-menu :clonombrese-on-content-click="false" offset-y transition="scale-transition" v-model="menuOpenDate">
                                    <template v-slot:activator="{ on }">
                                        <v-text-field @blur="openDate = parseDate(openDateFormatted)" :disabled="trade.automaticallyAdded" :label="$t('items.entryDate')" outlined persistent-hint
                                                      prepend-icon="fa-calendar" readonly v-model="openDateFormatted" v-on="on"/>
                                    </template>
                                    <v-date-picker @input="menuOpenDate = false" no-title v-model="openDate"/>
                                </v-menu>
                                <v-menu :close-on-content-click="false" offset-y transition="scale-transition" v-model="menuCloseDate">
                                    <template v-slot:activator="{ on }">
                                        <v-text-field :rules="closeDateRule" @blur="closeDate = parseDate(closeDateFormatted)" :disabled="trade.automaticallyAdded" :label="$t('items.closeDate')"
                                                      outlined persistent-hint prepend-icon="fa-calendar" readonly v-model="closeDateFormatted" v-on="on"/>
                                    </template>
                                    <v-date-picker @input="menuCloseDate = false" no-title v-model="closeDate"/>
                                </v-menu>
                            </v-col>
                            <v-col>
                                <v-menu ref="menu" v-model="menuOpenTime" :close-on-content-click="false" :nudge-right="40" :return-value.sync="openTime"
                                        transition="scale-transition" offset-y max-width="290px" min-width="290px">
                                    <template v-slot:activator="{ on }">
                                        <v-text-field v-model="openTime" :disabled="trade.automaticallyAdded" :label="$t('items.entryTime')" prepend-icon="fa-clock" readonly v-on="on" outlined></v-text-field>
                                    </template>
                                    <v-time-picker v-if="menuOpenTime" v-model="openTime" @click:minute="$refs.menu.save(openTime)"></v-time-picker>
                                </v-menu>
                                <v-menu ref="menu2" v-model="menuCloseTime" :close-on-content-click="false" :nudge-right="40" :return-value.sync="closeTime"
                                        transition="scale-transition" offset-y max-width="290px" min-width="290px">
                                    <template v-slot:activator="{ on }">
                                        <v-text-field v-model="closeTime" :disabled="trade.automaticallyAdded" :label="$t('items.closeTime')" prepend-icon="fa-clock" readonly v-on="on" outlined></v-text-field>
                                    </template>
                                    <v-time-picker v-if="menuCloseTime" v-model="closeTime" @click:minute="$refs.menu2.save(closeTime)"></v-time-picker>
                                </v-menu>
                            </v-col>
                            <v-col>
                                <v-text-field :rules="priceRule" :hint="$t('hintDigits')" :disabled="trade.automaticallyAdded" :label="$t('items.entryPrice')" outlined
                                              prepend-icon="fa-chart-line" type="number" v-model="tradeEntryPrice"/>
                                <v-text-field :rules="priceRule" :hint="$t('hintDigits')" :disabled="trade.automaticallyAdded" :label="$t('items.closePrice')" outlined
                                              prepend-icon="fa-chart-line" type="number" v-model="tradeClosePrice"/>
                            </v-col>
                        </v-row>

                        <v-divider class="mt-n5 mb-3"/>

                        <v-row>
                            <v-col>
                                <v-text-field :rules="priceRule" :hint="$t('hintDigits')" :label="$t('items.stopLoss')" outlined
                                              prepend-icon="fa-coins" type="number" v-model="tradeStopLossPrice"/>
                                <v-autocomplete :items="strategies" item-text="name" item-value="id" :return-object="false"
                                                :label="$t('items.strategy')" outlined prepend-icon="fa-bezier-curve" :rules="strategyRule" v-model="tradeStrategy"/>
                            </v-col>
                            <v-col>
                                <v-text-field :label="$t('items.grossProfit')" :hint="$t('hintProfit')" outlined :disabled="trade.automaticallyAdded" prepend-icon="fa-coins" type="number" v-model="tradeGrossProfit"/>
                                <v-autocomplete :items="this.$store.state.book.moods" :label="$t('items.mood')" outlined prepend-icon="fa-thermometer-half" :rules="moodRule" v-model="tradeMood"/>
                            </v-col>
                            <v-col>
                                <v-text-field :rules="commissionsRule" :label="$t('items.commissions')" outlined :disabled="trade.brokerAccount.verified && !(trade.platform === 'NinjaTrader' || trade.platform === 'ATAS')" prepend-icon="fa-coins" type="number" v-model="tradeCommissions"/>
                                <div class="d-flex align-center justify-center">
                                    <v-btn class="text-center mt-3" @click="saveTrade" outlined>{{ $t('btnSave') }}</v-btn>
                                </div>
                            </v-col>
                        </v-row>
                    </v-content>
                </v-form>
            </v-card-text>
        </div>
    </v-card>
</template>

<script>
    import Repository from '../../../repositories/journal';
    import { mapMutations } from 'vuex';
    export default {
        name: "EditTrade",
        props: {
            trade: Object,
        },
        data() {
            return {
                //Trade Time Avg Related
                tradeTimeAvg: 0,

                openDate: new Date().toISOString().substr(0, 10),
                closeDate: new Date().toISOString().substr(0, 10),
                openDateFormatted: this.formatDate(new Date().toISOString().substr(0, 10)),
                closeDateFormatted: this.formatDate(new Date().toISOString().substr(0, 10)),
                menuOpenDate: false,
                menuCloseDate: false,
                menuOpenTime: false,
                menuCloseTime: false,
                openTime: null,
                closeTime: null,

                tradeAsset: 0,
                tradeSide: '',
                tradeBrokerAccount: 0,
                tradeOpenTime: '',
                tradeCloseTime: '',
                tradeEntryPrice: 0,
                tradeClosePrice: 0,
                tradeStopLossPrice: 0,
                tradeGrossProfit: 0,
                tradeCommissions: 0,
                tradeStrategy: 0,
                tradeMood: '',

                // DATA
                brokerAccounts: [],
                strategies: [],

                //RULES AND VALIDATION
                valid: false,
                closeDateRule: [
                    // eslint-disable-next-line no-unused-vars
                    d => this.closeDate >= this.openDate || 'Close date must be after the open date'
                ],
                timeRule: [
                    t => !!t || 'Time is required',
                    t => t.length <= 5 || 'Add a valid HH:MM time',
                    t => /.+:.+/.test(t) || 'Add a valid HH:MM time',
                ],
                priceRule: [
                    p => p > 0 || 'Price must be greater than 0',
                ],
                commissionsRule: [
                    c => c >= 0 || 'Commissions must be equal or greater than 0',
                ],
                assetRule: [
                    a => !!a || 'Asset is required',
                ],
                brokerAccountRule: [
                    b => !!b || 'Broker account is required',
                ],
                strategyRule: [
                    s => !!s || 'Watchlist is required',
                ],
                moodRule: [
                    m => !!m || 'Mood is required',
                ],
            };
        },
        created() {
            this.init();
        },
        watch: {
            openDate() {
                this.openDateFormatted = this.formatDate(this.openDate);
            },
            closeDate() {
                this.closeDateFormatted = this.formatDate(this.closeDate);
            }
        },
        methods: {
            init() {
                this.strategies = [{ name: 'None', id: -7, }];
                if(this.trade.brokerAccount.verified)
                    this.brokerAccounts.push(this.trade.brokerAccount);
                else
                    this.$store.state.user.brokerAccounts.forEach((brokerAccount) => { if(!brokerAccount.verified) this.brokerAccounts.push(brokerAccount) });

                this.$store.state.user.strategies.forEach((strategy) => { this.strategies.push(strategy); });

                if(this.trade.strategy != null)
                    this.tradeStrategy = this.trade.strategy.id;
                else
                    this.tradeStrategy = this.strategies[0].id;

                this.tradeMood = this.trade.mood;
                this.tradeAsset = this.trade.asset.id;
                this.tradeBrokerAccount = this.trade.brokerAccount.id;
                this.tradeSide = this.trade.side;
                this.tradeEntryPrice = this.trade.entryPrice;
                this.tradeClosePrice = this.trade.closePrice;
                this.tradeStopLossPrice = this.trade.stopLossPrice;
                this.tradeGrossProfit = this.trade.netProfit + Math.abs(this.trade.commisions);
                this.tradeCommissions = Math.abs(this.trade.commisions);

                //Dates
                this.openDate = this.trade.openTime.toISOString().substr(0, 10);
                this.closeDate = this.trade.closeTime.toISOString().substr(0, 10);
                this.openTime = `${this.trade.openTime.getHours()}:${this.trade.openTime.getMinutes()}`;
                this.closeTime = `${this.trade.closeTime.getHours()}:${this.trade.closeTime.getMinutes()}`;
            },
            closeOverlay() {
                this.$emit('closeEditWindow');
            },
            ...mapMutations(['setTrades', 'setStats', 'setLoadingTrades', 'setTradesPageInfo']),
            saveTrade() {
                if(this.valid) {
                    let netProfit = ( this.tradeGrossProfit * 1 ) - Math.abs(this.tradeCommissions * 1 );
                    let pipValue = ( Math.abs(this.tradeGrossProfit * 1) / (Math.abs(this.tradeEntryPrice - this.tradeClosePrice ))).toFixed(2);
                    let openTimeArray = this.openTime.split(':');
                    let closeTimeArray = this.closeTime.split(':');

                    let trade = {
                        id: this.trade.id,
                        side: this.tradeSide.toUpperCase(),
                        mood: this.tradeMood.toUpperCase(),
                        openTime: new Date(this.openDate.substr(0, 4), this.openDate.substr(5, 2) - 1, this.openDate.substr(8, 2),
                            openTimeArray[0], openTimeArray[1]),
                        closeTime: new Date(this.closeDate.substr(0, 4), this.closeDate.substr(5, 2) - 1, this.closeDate.substr(8, 2),
                            closeTimeArray[0], closeTimeArray[1]),
                        entryPrice: ( this.tradeEntryPrice * 1 ),
                        closePrice: ( this.tradeClosePrice * 1 ),
                        stopLossPrice: ( this.tradeStopLossPrice * 1 ),
                        netProfit: netProfit,
                        pipValue: ( pipValue * 1 ),
                        commisions: ( this.tradeCommissions * 1 ),
                        user: { id: this.$store.state.user.id },
                        strategy: { id: this.tradeStrategy },
                        asset: { id: this.tradeAsset },
                        brokerAccount: { id: this.tradeBrokerAccount },
                        automaticallyAdded: this.trade.automaticallyAdded,
                        note: this.trade.note,
                        tradeImageUrl: this.trade.tradeImageUrl,
                    };

                    // Validate if the trade does not have a strategy
                    if(this.tradeStrategy === -7)
                        delete trade.strategy;
                    // Validate if the pipValue is NaN
                    if(isNaN(trade.pipValue))
                        trade.pipValue = 0;
                    this.setLoadingTrades(true);
                    Repository.updateTrade(trade).then((response) => {
                        if(response.status < 400){
                            let filter = this.$store.state.book.currentFilter;
                            Repository.filteredTradesOfUser(filter, this.$store.state.book.pageable).then((responseTrades) => {
                                let trades = responseTrades.data.content;
                                 if(trades.length>0){
                                    this.tradeTimeAvg = 0;
                                    trades.forEach((item) => {
                                        item.mood = item.mood[0] + item.mood.slice(1).toLowerCase();
                                        item.side = item.side[0] + item.side.slice(1).toLowerCase();
                                        item.tradeTime = this.convertDuration(item.tradeTime);
                                        item.openTime = new Date(item.openTime);
                                        item.closeTime = new Date(item.closeTime);
                                    });
                                    this.setTrades(responseTrades.data.content);
                                    let stats = {tradeTimeAvg: this.secondsToHHMMSS(this.tradeTimeAvg / trades.length)};
                                    this.setStats(stats);
                                    this.setTradesPageInfo({totalElements: responseTrades.data.totalElements, totalPages: responseTrades.data.totalPages});
                                }else{
                                    Repository.allTradesOfUser(this.$store.state.book.pageable).then((responseAllTrades) => {
                                        let trades = responseAllTrades.data.content;
                                        this.tradeTimeAvg = 0;
                                        trades.forEach((item) => {
                                            item.mood = item.mood[0] + item.mood.slice(1).toLowerCase();
                                            item.side = item.side[0] + item.side.slice(1).toLowerCase();
                                            item.tradeTime = this.convertDuration(item.tradeTime);
                                            item.openTime = new Date(item.openTime);
                                            item.closeTime = new Date(item.closeTime);
                                            //item.openTime = this.convertUTCDateToLocalDate(new Date(item.openTime));
                                        });
                                        this.setTrades(responseAllTrades.data.content);
                                        
                                        let stats = {tradeTimeAvg: this.secondsToHHMMSS(this.tradeTimeAvg / trades.length)};
                                        if (trades.length > 0) {
                                            this.setStats(stats);
                                            this.setNoTrades(false);
                                        } else{
                                            this.setNoTrades(true);
                                        }
                                            this.setTradesPageInfo({totalElements: responseAllTrades.data.totalElements, totalPages: responseAllTrades.data.totalPages});
                                         });
                                    }
                           });
                        }
                    });

                    this.$emit('closeEditWindow');
                }
                else {
                    alert('There are one or more mandatory fields that are not filled.');
                }
            },
            convertDuration(isoFormat){
                let iso8601DurationRegex = /(-)?P(?:([.,\d]+)Y)?(?:([.,\d]+)M)?(?:([.,\d]+)W)?(?:([.,\d]+)D)?T(?:([.,\d]+)H)?(?:([.,\d]+)M)?(?:([.,\d]+)S)?/;
                let matches = isoFormat.match(iso8601DurationRegex);

                // Sum to the trade time average
                this.tradeTimeAvg += (matches[8] === undefined ? 0 : matches[8] * 1) +
                    (matches[7] === undefined ? 0 : matches[7] * 60) +
                    (matches[6] === undefined ? 0 : matches[6] * 3600) +
                    (matches[5] === undefined ? 0 : matches[5] * 86400) +
                    (matches[4] === undefined ? 0 : matches[4] * 604800);

                return {
                    sign: matches[1] === undefined ? '+' : '-',
                    years: matches[2] === undefined ? 0 : matches[2],
                    months: matches[3] === undefined ? 0 : matches[3],
                    weeks: matches[4] === undefined ? 0 : matches[4],
                    days: matches[5] === undefined ? 0 : matches[5],
                    hours: matches[6] === undefined ? 0 : matches[6],
                    minutes: matches[7] === undefined ? 0 : matches[7],
                    seconds: matches[8] === undefined ? 0 : matches[8]
                };
            },
            convertUTCDateToLocalDate(date) {
                let newDate = new Date(date.getTime() + date.getTimezoneOffset()*60*1000);
                let offset = date.getTimezoneOffset() / 60;
                let hours = date.getHours();
                newDate.setHours(hours - offset);
                return newDate;
            },
            secondsToHHMMSS(secondsToConvert) {
                let sec_num = parseInt(secondsToConvert, 10); // don't forget the second param
                let hours = Math.floor(sec_num / 3600);
                let minutes = Math.floor((sec_num - (hours * 3600)) / 60);
                let seconds = sec_num - (hours * 3600) - (minutes * 60);

                if (hours < 10)
                    hours = "0" + hours;
                if (minutes < 10)
                    minutes = "0" + minutes;
                if (seconds < 10)
                    seconds = "0" + seconds;

                return `${hours}h ${minutes}m ${seconds}s`;
            },
            formatDate(date) {
                if (!date) return null;

                const [year, month, day] = date.split('-');
                return `${month}/${day}/${year}`
            },
            parseDate(date) {
                if (!date) return null;

                const [month, day, year] = date.split('/');
                return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
            },
        },
    }
</script>

<i18n>
{
  "en": {
    "titles": {
      "normal": "Edit Trade"
    },
    "items": {
      "asset": "Asset",
      "account": "Broker Account",
      "side": "Side",
      "entryDate": "Open Date",
      "entryTime": "Open Time",
      "entryPrice": "Entry Price",
      "closeDate": "Close Date",
      "closeTime": "Close Time",
      "closePrice": "Close Price",
      "stopLoss": "Stop Loss Price",
      "grossProfit": "Gross Profit",
      "commissions": "Trade Commissions",
      "strategy": "Trading Strategy",
      "mood": "Mood"
    },
    "hintDigits": "Please add all the digits",
    "hintProfit": "Gross Profit in USD",
    "btnSave": "Save Trade"
  },
  "es": {
    "titles": {
      "normal": "Editar Trade"
    },
    "items": {
      "asset": "Activo",
      "account": "Cuenta de Broker",
      "side": "Lado",
      "entryDate": "Fecha de Apertura",
      "entryTime": "Tiempo de Apertura",
      "entryPrice": "Precio de Apertura",
      "closeDate": "Fecha de Cierre",
      "closeTime": "Tiempo de Cierre",
      "closePrice": "Precio de Cierre",
      "stopLoss": "Precio de Stop Loss",
      "grossProfit": "Beneficio Bruto",
      "commissions": "Comisiones del Trade",
      "strategy": "Estrategia de Trading",
      "mood": "Sentimiento"
    },
    "hintDigits": "Agrega todos los dígitos",
    "hintProfit": "Beneficio en USD",
    "btnSave": "Guardar Trade"
  }
}
</i18n>

<style scoped>

</style>
